import React, { useMemo } from 'react';
import { Quota, Criterion, ParticipantFieldChoice } from 'queries';
import { Theme } from '@app/theme';
import { createUseStyles } from 'react-jss';
import { ErrorMsg } from '@app/form';
import { useTranslation } from 'react-i18next';
import { QuotaInput } from './../../Quota/QuotaInput';

const useStyles = createUseStyles((theme: Theme) => ({
  container: {
    padding: theme.marginBase * 2,
    backgroundColor: theme.colors.white,
    border: `1px solid ${theme.colors.grayBorder}`,
    borderRadius: theme.borderRadius.std,
  },
  inputList: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.marginBase * 2,
  },
  warning: {
    marginTop: theme.marginBase / 2,
  },
}));

interface Props {
  criterion: Criterion;
  options: ParticipantFieldChoice[];
  numberOfParticipantRequired: number;
  onChange: (val: Criterion) => any;
}

export const CriterionQuotaList = ({
  criterion,
  options,
  numberOfParticipantRequired,
  onChange,
}: Props) => {
  const classes = useStyles();
  const { t } = useTranslation();

  const optionsMap = useMemo(() => {
    const map = new Map<string, string>();
    options.forEach((option) => {
      map.set(option.value.toString(), option.label.toString());
    });
    return map;
  }, [options]);

  const quotas = criterion.quotas ?? [];
  const setQuotas = (quotas: Quota[]) => {
    onChange({
      ...criterion,
      quotas,
    });
  };

  const handleChange = (index: number, newValue: number) => {
    const updated = [...quotas];
    updated[index] = { ...updated[index], value: newValue };
    setQuotas(updated);
  };

  const sum = quotas.reduce((acc, field) => acc + (field.value || 0), 0);

  const isInvalidMessage =
    sum !== numberOfParticipantRequired
      ? sum > numberOfParticipantRequired
        ? t('editSession.targeting.quotas.overly', {
            count: sum,
            required: numberOfParticipantRequired,
          })
        : t('editSession.targeting.quotas.insufficient', {
            count: sum,
            required: numberOfParticipantRequired,
          })
      : false;

  return (
    <div className={classes.container}>
      <div className={classes.inputList}>
        {quotas.map((field: Quota, index) => (
          <QuotaInput
            value={field.value}
            label={
              optionsMap.get(field.item.toString()) ?? field.item.toString()
            }
            onChange={(newValue) => handleChange(index, newValue)}
          />
        ))}
      </div>
      {!!isInvalidMessage && (
        <ErrorMsg
          className={classes.warning}
          variant="warn"
          error={isInvalidMessage}
        />
      )}
    </div>
  );
};
